'use client';

import type { Breakpoint } from '@mui/material/styles';

import { useBoolean } from 'minimal-shared/hooks';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

import { paths } from 'src/routes/paths';

import { Logo } from 'src/components/logo';

import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { MainSection } from '../core/main-section';
import { MainFooter } from './footer';

import { Drawer, Fab, IconButton } from '@mui/material';
import { Phone } from 'lucide-react';

import { useRouter } from 'next/navigation';
import { grey, primary, secondary, themeConfig } from 'src/theme';
import type { HeaderSectionProps } from '../core/header-section';
import type { LayoutSectionProps } from '../core/layout-section';
import type { MainSectionProps } from '../core/main-section';
import { NAV_ITEMS } from '../nav-items';
import { NavBasicDesktop, NavBasicMobile } from './nav-basic';
import IconMenuItem from '/public/assets/icons/navbar/ic-menu-item.svg';

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type MainLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    main?: MainSectionProps;
  };
};

export const MainLayout = ({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'md',
}: MainLayoutProps) => {
  const router = useRouter();
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const renderHeader = () => {
    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <Drawer
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: 280, background: grey[900] } }}
          >
            <Box sx={{ pl: 2.5, py: 2, width: '220px' }}>
              <Logo height="auto" isSingle={false} onClick={() => router.push(paths.home)} />
            </Box>

            <NavBasicMobile
              sx={{ px: 1.5 }}
              data={NAV_ITEMS}
              cssVars={{ '--nav-item-gap': '8px' }}
              slotProps={{
                rootItem: {
                  sx: {},
                  icon: {},
                  texts: {},
                  title: {
                    color: 'white',
                    '&:hover': {
                      color: secondary.main,
                    },
                  },
                  caption: {},
                  info: {},
                  arrow: {},
                },
                subItem: {
                  sx: {},
                  icon: {},
                  texts: {},
                  title: {
                    color: 'white',
                  },
                  caption: {},
                  info: {},
                  arrow: {},
                },
              }}
            />
          </Drawer>
          <Box
            onClick={() => router.push(paths.home)}
            sx={{
              width: '150px',
              p: '4px 12px 8px',
              border: `2px solid ${themeConfig.palette.primary.main}`,
              borderRadius: '50px',
              cursor: 'pointer',
            }}
          >
            <Logo height="auto" isSingle={false} onClick={() => router.push(paths.home)} />
          </Box>
        </>
      ),
      rightArea: (
        <>
          {/** @slot Nav desktop */}
          <Box sx={{ marginRight: '40px', display: { xs: 'none', md: 'block' } }}>
            <NavBasicDesktop
              data={NAV_ITEMS}
              cssVars={{ '--nav-item-gap': '16px' }}
              slotProps={{
                rootItem: {
                  sx: {},
                  icon: {},
                  texts: {},
                  title: {
                    '&:hover': {
                      color: primary.main,
                    },
                  },
                  caption: {},
                  arrow: {
                    '&:hover': {
                      color: primary.main,
                    },
                  },
                },
                subItem: {
                  sx: {},
                  icon: {},
                  texts: {},
                  title: {},
                  caption: {},
                  arrow: {},
                },
                dropdown: { paper: {} },
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
            <Fab
              variant="outlinedExtended"
              color="primary"
              rel="noopener"
              target="_blank"
              size="medium"
              href="https://wa.me/628161464888"
              sx={(theme) => ({
                display: 'none',
                [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
              })}
            >
              <Phone />
              Hubungi Kami
            </Fab>
          </Box>

          <IconButton sx={{ display: { md: 'none' } }} onClick={onOpen} aria-label="Open menu">
            <IconMenuItem width={24} height={24} style={{ fill: secondary.main }} alt="icon-menu" />
          </IconButton>
        </>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={slotProps?.header?.slotProps}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderFooter = () => <MainFooter />;

  const renderMain = () => <MainSection {...slotProps?.main}>{children}</MainSection>;

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={cssVars}
      sx={sx}
    >
      {renderMain()}
    </LayoutSection>
  );
};
